import useSWR, { type MutatorCallback, type MutatorOptions, type SWRConfiguration, useSWRConfig } from "swr";
import useSWRInfinite, { type SWRInfiniteConfiguration, unstable_serialize } from "swr/infinite";

import type { HttpClient } from "./http-client";
import type { OpenApiEndpointMethods, OpenApiPaths } from "./http-client-openapi.types";
import { matchSwrKey } from "./http-client-swr-key-matcher";
import { buildEndpointUrl } from "./http-client-url-builder";
import type { HttpClientFetchOptions, HttpClientResult, HttpClientUrlMatchOptions } from "./http-client.types";

type SupportedMethods = "get" | "post" | "put" | "patch" | "delete";

type HttpClientFn<P extends OpenApiPaths, E extends keyof P, M extends OpenApiEndpointMethods<P[E]>> = (
  e: E,
  o: HttpClientFetchOptions<P[E][M]>,
) => Promise<HttpClientResult<P[E][M]>>;

interface HttpClientSwrInfiniteKey {
  endpoint: string;
  path: Record<string, unknown>;
  query: object;
}

export const useHttpClientSWR = <P extends OpenApiPaths, E extends keyof P, M extends OpenApiEndpointMethods<P[E]> & SupportedMethods>(
  client: HttpClient<P>,
  endpoint: E,
  method: M,
  httpOptions: HttpClientFetchOptions<P[E][M]>,
  swrOptions?: SWRConfiguration,
  shouldFetch = true,
) => {
  const { path, query } = httpOptions as {
    path?: Record<string, string | number>;
    query?: object;
  };

  const swrKey = buildEndpointUrl(client.baseUrl, endpoint as string, path ?? {}, query ?? {});

  const fetchFn = client as Record<SupportedMethods, HttpClientFn<P, E, M>>;

  const keyPath = swrKey.replace(client.baseUrl, "");
  const tenantId = client.options.tenantId;
  const finalKey = tenantId ? `${keyPath}@@${tenantId}` : keyPath;

  return useSWR(shouldFetch ? finalKey : null, () => fetchFn[method](endpoint, httpOptions), swrOptions);
};

export const useHttpClientSWRMutation = <
  P extends OpenApiPaths,
  E extends keyof P,
  M extends OpenApiEndpointMethods<P[E]> & SupportedMethods,
  T = unknown,
>(
  endpoint: E,
  urlOptions: HttpClientUrlMatchOptions<P[E][M]> | ((page: number) => HttpClientUrlMatchOptions<P[E][M]>),
  data: T | Promise<T> | MutatorCallback<T> = (data) => data,
  opts: MutatorOptions = {},
) => {
  const { mutate } = useSWRConfig();

  if (urlOptions instanceof Function) {
    const keyFn = (page: number): HttpClientSwrInfiniteKey => {
      const { path, query } = urlOptions(page) as {
        path?: Record<string, string | number>;
        query?: object;
      };

      return {
        endpoint: endpoint as string,
        path: {
          ...(path ?? {}),
        },
        query: {
          ...(query ?? {}),
        },
      };
    };

    return () => mutate(unstable_serialize(keyFn), data, opts);
  }

  const { path, query } = urlOptions as {
    path?: Record<string, string | number>;
    query?: object;
  };

  return () =>
    mutate(
      (key) => {
        if (typeof key !== "string") {
          return false;
        }

        return matchSwrKey(key, { endpoint: endpoint as string, path, query });
      },
      data,
      opts,
    );
};

export const useHttpClientSWRInfinite = <
  P extends OpenApiPaths,
  E extends keyof P,
  M extends OpenApiEndpointMethods<P[E]> & SupportedMethods,
>(
  client: HttpClient<P>,
  endpoint: E,
  method: M,
  getKey: (pageIndex: number) => HttpClientFetchOptions<P[E][M]>,
  swrOptions?: SWRInfiniteConfiguration,
) => {
  const fetchFn = client as Record<SupportedMethods, HttpClientFn<P, E, M>>;

  const keyFn = (pageIndex: number): HttpClientSwrInfiniteKey => {
    const key = getKey(pageIndex);

    return {
      endpoint: endpoint as string,
      path: {
        ...((key.path as object) ?? {}),
      },
      query: {
        ...((key.query as object) ?? {}),
      },
    };
  };

  return useSWRInfinite<HttpClientResult<P[E][M]>>(
    keyFn,
    (httpOptions: HttpClientFetchOptions<P[E][M]>) => fetchFn[method](endpoint, httpOptions),
    swrOptions,
  );
};
